import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useAppAuth } from '../auth/appAuth';

const API_BASE_URL = process.env.REACT_APP_API_SERVER_URL || 'https://127.0.0.1:5005';
const DEV_BYPASS_AUTH = process.env.REACT_APP_BYPASS_AUTH === 'true';

interface QuotaStatus {
  usage_percentage: number;
  actual_percentage?: number;
  reset_date: string;
  is_over_limit: boolean;
  is_unlimited: boolean;
}

interface QuotaBarProps {
  /** Callback when quota is exceeded */
  onQuotaExceeded?: () => void;
  /** Compact mode for small spaces */
  compact?: boolean;
  /** Refresh trigger - increment to force refresh */
  refreshTrigger?: number;
}

/**
 * QuotaBar - Displays user's chat quota usage as a progress bar
 * 
 * Features:
 * - Shows usage percentage (0-100%)
 * - Tooltip shows reset date on hover
 * - Color changes based on usage level:
 *   - Green: 0-50%
 *   - Yellow: 50-80%
 *   - Orange: 80-95%
 *   - Red: 95%+
 * - Hidden when user has unlimited access
 */
const QuotaBar: React.FC<QuotaBarProps> = ({ 
  onQuotaExceeded, 
  compact = false,
  refreshTrigger = 0 
}) => {
  const [quota, setQuota] = useState<QuotaStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { getAccessTokenSilently, isAuthenticated } = useAppAuth();

  const fetchQuota = useCallback(async () => {
    if (!isAuthenticated && !DEV_BYPASS_AUTH) {
      setLoading(false);
      return;
    }

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (!DEV_BYPASS_AUTH && isAuthenticated) {
        try {
          const token = await getAccessTokenSilently();
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
        } catch (tokenError) {
          console.warn('Could not get auth token for quota:', tokenError);
        }
      }

      const response = await fetch(`${API_BASE_URL}/api/chat/quota`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setQuota(data);
        setError(null);
        return data; // Return data for callback handling
      } else if (response.status === 401) {
        setError('Not authenticated');
      } else {
        setError('Failed to load quota');
      }
    } catch (err) {
      console.error('Error fetching quota:', err);
      setError('Connection error');
    } finally {
      setLoading(false);
    }
    return null;
  }, [getAccessTokenSilently, isAuthenticated]); // Removed onQuotaExceeded from deps

  // Fetch on mount only
  useEffect(() => {
    fetchQuota().then(data => {
      if (data?.is_over_limit && onQuotaExceeded) {
        onQuotaExceeded();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Refresh when trigger changes (after sending a message)
  // Use a ref to track the last trigger value to prevent double-fetches
  const lastTriggerRef = React.useRef(refreshTrigger);
  useEffect(() => {
    if (refreshTrigger > 0 && refreshTrigger !== lastTriggerRef.current) {
      lastTriggerRef.current = refreshTrigger;
      fetchQuota().then(data => {
        if (data?.is_over_limit && onQuotaExceeded) {
          onQuotaExceeded();
        }
      });
    }
  }, [refreshTrigger, fetchQuota, onQuotaExceeded]);

  // Don't show anything while loading or on error
  if (loading || error || !quota) {
    return null;
  }

  // Don't show for unlimited users
  if (quota.is_unlimited) {
    return null;
  }

  // Calculate color based on usage
  const getBarColor = (percentage: number): string => {
    if (percentage >= 95) return '#ff4444'; // Red
    if (percentage >= 80) return '#ff8c00'; // Orange
    if (percentage >= 50) return '#ffd700'; // Yellow/Gold
    return '#4ade80'; // Green
  };

  // Format reset date for tooltip
  const formatResetDate = (isoDate: string): string => {
    const date = new Date(isoDate);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Resets today';
    if (diffDays === 1) return 'Resets tomorrow';
    if (diffDays < 7) return `Resets in ${diffDays} days`;
    
    return `Resets ${date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    })}`;
  };

  const percentage = Math.min(quota.usage_percentage, 100);
  const barColor = getBarColor(percentage);
  const resetText = formatResetDate(quota.reset_date);

  const tooltipContent = (
    <Box sx={{ textAlign: 'center', padding: '4px 0' }}>
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
        Chat Quota: {percentage.toFixed(0)}% used
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
        {resetText}
      </Typography>
      {quota.is_over_limit && (
        <Typography variant="caption" sx={{ display: 'block', color: '#ff6b6b', mt: 0.5 }}>
          Limit reached - wait for reset
        </Typography>
      )}
    </Box>
  );

  if (compact) {
    // Compact mode: just a thin bar
    return (
      <Tooltip title={tooltipContent} arrow placement="bottom">
        <Box
          sx={{
            width: '100%',
            height: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 1.5,
            overflow: 'hidden',
            cursor: 'help',
          }}
        >
          <Box
            sx={{
              width: `${percentage}%`,
              height: '100%',
              backgroundColor: barColor,
              transition: 'width 0.3s ease, background-color 0.3s ease',
            }}
          />
        </Box>
      </Tooltip>
    );
  }

  // Full mode: bar with label
  return (
    <Tooltip title={tooltipContent} arrow placement="bottom">
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'help',
        }}
      >
        <Box
          sx={{
            flex: 1,
            height: 6,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: 3,
            overflow: 'hidden',
            minWidth: 60,
            maxWidth: 100,
          }}
        >
          <Box
            sx={{
              width: `${percentage}%`,
              height: '100%',
              backgroundColor: barColor,
              transition: 'width 0.3s ease, background-color 0.3s ease',
            }}
          />
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: barColor,
            fontSize: '0.65rem',
            fontWeight: 600,
            minWidth: 28,
          }}
        >
          {percentage.toFixed(0)}%
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default QuotaBar;

